import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Typography,
    alpha,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { getApiBaseUrl, resolveUploadedMediaUrl } from "../../config/api";
import { getStudentClassId, getStudentClassLabel } from "../../utils/userContext";
import { StudentPageHeader } from "./studentPortalUi";

const TYPE_OPTIONS = ["All", "PastPaper", "Notes", "Book", "TLM"];
const TYPE_LABELS = {
    PastPaper: "Past papers",
    Notes: "Notes",
    Book: "Books",
    TLM: "TLMs",
};
const TYPE_CHIP = {
    PastPaper: "primary",
    Notes: "info",
    Book: "secondary",
    TLM: "warning",
};
const TYPE_ICON = {
    PastPaper: ArticleOutlinedIcon,
    Notes: DescriptionOutlinedIcon,
    Book: MenuBookOutlinedIcon,
    TLM: MenuBookOutlinedIcon,
};

function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
    return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

const StudentResources = () => {
    const { currentUser, userDetails } = useSelector((s) => s.user);
    const classId = getStudentClassId(currentUser, userDetails);
    const classLabel = getStudentClassLabel(currentUser, userDetails) || "your class";

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [typeFilter, setTypeFilter] = useState("All");
    const [subjectFilter, setSubjectFilter] = useState("All");

    const loadSubjects = useCallback(async () => {
        if (!classId) return;
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/ClassSubjects/${classId}`);
            setSubjects(Array.isArray(data) ? data : []);
        } catch {
            setSubjects([]);
        }
    }, [classId]);

    const loadResources = useCallback(async () => {
        if (!classId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("currentTerm", "true");
            if (typeFilter !== "All") params.append("resourceType", typeFilter);
            if (subjectFilter !== "All") params.append("subjectId", subjectFilter);
            const qs = params.toString() ? `?${params.toString()}` : "";
            const { data } = await axios.get(`${getApiBaseUrl()}/TeachingResources/class/${classId}${qs}`);
            setRows(Array.isArray(data) ? data : []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [classId, typeFilter, subjectFilter]);

    useEffect(() => {
        loadSubjects();
    }, [loadSubjects]);

    useEffect(() => {
        loadResources();
    }, [loadResources]);

    const grouped = useMemo(() => {
        const map = { PastPaper: [], Notes: [], Book: [], TLM: [] };
        rows.forEach((row) => {
            const t = row.resourceType;
            if (map[t]) map[t].push(row);
        });
        return map;
    }, [rows]);

    const renderCard = (row) => {
        const id = row.id || row._id;
        const Icon = TYPE_ICON[row.resourceType] || DescriptionOutlinedIcon;
        const downloadUrl = resolveUploadedMediaUrl(row.fileUrl);
        const subName = row.subject?.subName || "—";

        return (
            <Grid item xs={12} sm={6} md={4} key={id}>
                <Card
                    elevation={0}
                    sx={{
                        height: "100%",
                        borderRadius: 2.5,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        transition: (t) => t.transitions.create("box-shadow"),
                        "&:hover": {
                            boxShadow: (t) => `0 6px 20px ${alpha(t.palette.common.black, 0.08)}`,
                        },
                    }}
                >
                    <CardContent>
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                    color: "primary.main",
                                }}
                            >
                                <Icon fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                    {row.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {subName}
                                </Typography>
                            </Box>
                        </Stack>
                        {row.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
                                {row.description}
                            </Typography>
                        ) : null}
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                            <Chip
                                size="small"
                                label={TYPE_LABELS[row.resourceType] || row.resourceType}
                                color={TYPE_CHIP[row.resourceType] || "default"}
                                variant="outlined"
                            />
                            {row.term ? <Chip size="small" label={row.term} variant="outlined" /> : null}
                            {row.academicYear ? (
                                <Chip size="small" label={row.academicYear} variant="outlined" />
                            ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            {row.fileName} · {formatBytes(row.fileSize)} · {formatDate(row.createdAt)}
                        </Typography>
                        <Button
                            component="a"
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="small"
                            startIcon={<DownloadOutlinedIcon />}
                            fullWidth
                            sx={{ fontWeight: 700 }}
                        >
                            Download
                        </Button>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <StudentPageHeader
                    eyebrow="Study materials"
                    title="Class resources"
                    subtitle={`Past papers, notes, and books shared by your teachers for ${classLabel}.`}
                />

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2.5,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                {TYPE_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t === "All" ? "All types" : TYPE_LABELS[t] || t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Subject</InputLabel>
                            <Select
                                label="Subject"
                                value={subjectFilter}
                                onChange={(e) => setSubjectFilter(e.target.value)}
                            >
                                <MenuItem value="All">All subjects</MenuItem>
                                {subjects.map((s) => (
                                    <MenuItem key={s._id || s.id} value={s._id || s.id}>
                                        {s.subName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Paper>

                {loading ? (
                    <Grid container spacing={2}>
                        {[1, 2, 3].map((i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Skeleton variant="rounded" height={200} />
                            </Grid>
                        ))}
                    </Grid>
                ) : rows.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: "center",
                            borderRadius: 2.5,
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        }}
                    >
                        <MenuBookOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                        <Typography color="text.secondary">
                            No resources available yet. Your teachers may upload materials here soon.
                        </Typography>
                    </Paper>
                ) : typeFilter === "All" ? (
                    <Stack spacing={4}>
                        {(["PastPaper", "Notes", "Book"]).map((type) =>
                            grouped[type]?.length ? (
                                <Box key={type}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                        {TYPE_LABELS[type]}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {grouped[type].map(renderCard)}
                                    </Grid>
                                </Box>
                            ) : null
                        )}
                    </Stack>
                ) : (
                    <Grid container spacing={2}>
                        {rows.map(renderCard)}
                    </Grid>
                )}
            </Container>
        </Box>
    );
};

export default StudentResources;
